import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { FeatureFlag, Res } from '@cbo/shared-library';
import { useUserPreferencesContext, UserPreferencesTheme } from '@ncr-voyix-commerce/react-common-components';
import { useFeatureFlag } from '../utils/hooks/useFeatureFlag';

export interface AccountPreferences extends Res.Admin.UserPreferences {
  darkMode?: boolean;
  darkModeText?: string;
  theme?: UserPreferencesTheme | undefined;
  defaultOrganizationId?: string | undefined;
  username?: string | undefined;
}

export interface IAccountPreferencesContext {
  preferences: AccountPreferences | null;
  arePreferencesLoading: boolean | null;
  setPreferences: (preferences: AccountPreferences | null) => void;
  setPreferencesLoading: (isLoading: boolean | null) => void;
}

export const AccountPreferencesContext = createContext<IAccountPreferencesContext>({
  preferences: null,
  arePreferencesLoading: null,
  setPreferences: (_preferences) => null,
  setPreferencesLoading: (_isLoading) => null,
});

AccountPreferencesContext.displayName = 'AccountPreferencesContext';

export const useAccountPreferences = () => useContext(AccountPreferencesContext);

type AccountPreferencesContextProviderProps = {
  preferencesToSet: AccountPreferences | null;
  children: React.ReactNode;
  setDarkMode: (darkMode: boolean) => void;
};

export function AccountPreferencesContextProvider({
  preferencesToSet,
  children,
  setDarkMode,
}: AccountPreferencesContextProviderProps) {
  const { userPreferences } = useUserPreferencesContext();
  const darkModeEnabled = useFeatureFlag(FeatureFlag.darkMode);
  const [preferences, setPreferences] = useState<AccountPreferences | null>(preferencesToSet);
  const [arePreferencesLoading, setPreferencesLoading] = useState<boolean | null>(null);
  const [isSystemThemeDark, setIsSystemThemeDark] = useState<boolean>(false);
  const finalPreferences = useMemo(
    () => ({
      preferences,
      arePreferencesLoading,
      setPreferences,
      setPreferencesLoading,
    }),
    [preferences, arePreferencesLoading]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsSystemThemeDark(mediaQuery.matches);
    const handleChange = () => setIsSystemThemeDark(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    const prefersDarkMode =
      userPreferences?.theme === 'dark' || (userPreferences?.theme === 'system' && isSystemThemeDark);
    setDarkMode(darkModeEnabled && prefersDarkMode);
  }, [userPreferences, darkModeEnabled, setDarkMode, isSystemThemeDark]);
  return <AccountPreferencesContext.Provider value={finalPreferences}>{children}</AccountPreferencesContext.Provider>;
}

export default AccountPreferencesContextProvider;
